import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Download, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@reflex/ui/components/ui/select';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { ErrorState } from '@reflex/ui/components/shared/ErrorState';
import { LoadingState } from '@reflex/ui/components/shared/LoadingState';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { SettingsRow } from '@reflex/ui/components/shared/SettingsRow';
import { ActionRow } from '@reflex/ui/components/shared/ActionRow';
import { AdminSubPageLayout } from '@reflex/ui/components/layout/AdminSubPageLayout';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { toast } from 'sonner';
import { mutateWithToast } from '@/lib/mutation-helpers';
import { cn } from '@reflex/ui/lib/utils';
import { queryKeys } from '@/lib/query-keys';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import { THEME_REGISTRY } from '@reflex/plugin-themes/web/themes';
import { useColorTheme } from '@reflex/plugin-themes/web/useColorTheme';
import {
  deleteCustomTheme,
  getCustomThemes,
  getDefaultTheme,
  getTweakcnCatalog,
  importTweakcnTheme,
  setDefaultTheme,
} from '@reflex/plugin-themes/web/api';

export function AdminAppearancePage() {
  const queryClient = useQueryClient();
  const { themes: allThemes } = useColorTheme();
  const [search, setSearch] = useState('');
  // Both endpoints filter by the active org, so the cache keys carry it —
  // otherwise switching orgs paints the previous org's default for a frame.
  const orgKey = useOrgKey();

  const defaultThemeQuery = useQuery({
    queryKey: queryKeys.defaultTheme(orgKey),
    queryFn: getDefaultTheme,
  });

  const customThemesQuery = useQuery({
    queryKey: queryKeys.customThemes(orgKey),
    queryFn: getCustomThemes,
  });

  const catalogQuery = useQuery({
    queryKey: queryKeys.tweakcnCatalog,
    queryFn: getTweakcnCatalog,
    staleTime: 10 * 60 * 1000,
  });

  const setDefaultMutation = useMutation({
    mutationFn: (themeId: string) => setDefaultTheme(themeId),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.defaultTheme(orgKey), data);
      toast.success('Default theme updated');
    },
    onError: (err: Error) =>
      toast.error('Failed to update default theme', { description: err.message }),
  });

  const invalidateCustomThemes = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.customThemes(orgKey) });
  const importMutation = useMutation({
    mutationFn: (name: string) => importTweakcnTheme(name),
    onSuccess: invalidateCustomThemes,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteCustomTheme(id),
    onSuccess: invalidateCustomThemes,
  });

  const currentDefault = defaultThemeQuery.data?.themeId ?? 'neutral';
  const installedNames = new Set([
    ...THEME_REGISTRY.map((t) => t.id),
    ...(customThemesQuery.data ?? []).map((t) => t.name),
  ]);

  const catalogItems = (catalogQuery.data ?? []).filter(
    (item) =>
      item.label.toLowerCase().includes(search.toLowerCase()) ||
      item.name.includes(search.toLowerCase()),
  );

  return (
    <AdminSubPageLayout
      title="Appearance"
      description="Configure the default look and feel for this organization. Members can override it in their personal settings."
    >
      <AsyncContent
        isLoading={defaultThemeQuery.isLoading}
        isError={defaultThemeQuery.isError}
        loadingMessage="Loading appearance settings..."
        errorMessage="Failed to load appearance settings."
      >
        <div className="space-y-6">
          <SettingsRow
            title="Default Color Theme"
            description="The color theme applied for users who have not chosen their own."
            control={
              <Select
                value={currentDefault}
                onValueChange={(value) => setDefaultMutation.mutate(value)}
                disabled={setDefaultMutation.isPending}
              >
                <SelectTrigger className="w-40" aria-label="Default color theme">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allThemes.map((theme) => (
                    <SelectItem key={theme.id} value={theme.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-3 w-3 rounded-full border border-border"
                          style={{ backgroundColor: theme.accentColor }}
                        />
                        {theme.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />

          {(customThemesQuery.data ?? []).length > 0 && (
            <SectionCard
              title="Imported Themes"
              description="Themes imported from the tweakcn catalog. These are available to everyone in this organization."
            >
              <div className="space-y-2">
                {(customThemesQuery.data ?? []).map((theme) => (
                  <ActionRow
                    key={theme.id}
                    density="compact"
                    className="rounded-md border"
                    leading={
                      <span
                        className="block h-4 w-4 rounded-full border border-border"
                        style={{
                          backgroundColor:
                            theme.cssVars.dark?.['--primary'] ?? 'var(--muted-foreground)',
                        }}
                      />
                    }
                    title={theme.label}
                    titleAdornment={
                      <span className="text-3xs text-muted-foreground">{theme.source}</span>
                    }
                    actions={
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() =>
                          mutateWithToast(deleteMutation, theme.id, {
                            success: 'Theme removed',
                            error: 'Failed to remove theme',
                          })
                        }
                        disabled={deleteMutation.isPending}
                        aria-label={`Delete theme ${theme.label}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    }
                  />
                ))}
              </div>
            </SectionCard>
          )}

          <SectionCard
            title="Theme Catalog"
            description={
              <>
                Browse and import themes from{' '}
                <a
                  href="https://tweakcn.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 underline underline-offset-2"
                >
                  tweakcn.com <ExternalLink className="h-2.5 w-2.5" />
                </a>
                . Imported themes become available to everyone in this organization.
              </>
            }
          >
            <Input
              placeholder="Search themes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />

            {catalogQuery.isLoading ? (
              <LoadingState message="Loading catalog..." className="mt-4" />
            ) : catalogQuery.isError ? (
              <ErrorState
                message="Failed to load the tweakcn catalog. The server may be unreachable."
                className="mt-4 py-0"
              />
            ) : (
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                {catalogItems.map((item) => {
                  const isInstalled = installedNames.has(item.name);
                  return (
                    <button
                      key={item.name}
                      type="button"
                      disabled={isInstalled || importMutation.isPending}
                      onClick={() =>
                        mutateWithToast(importMutation, item.name, {
                          success: 'Theme imported',
                          error: 'Failed to import theme',
                        })
                      }
                      className={cn(
                        'group relative flex flex-col items-center gap-1.5 rounded-lg border p-3 text-left transition-colors',
                        isInstalled
                          ? 'border-primary/20 bg-primary/5 cursor-default'
                          : 'border-border hover:border-muted-foreground/30 hover:bg-muted/50 cursor-pointer',
                      )}
                    >
                      <div
                        className="h-8 w-full rounded-md"
                        style={{ backgroundColor: item.previewColor ?? 'var(--muted-foreground)' }}
                      />
                      <PanelTitle
                        as="span"
                        size="sm"
                        className="w-full truncate text-center text-xs"
                      >
                        {item.label}
                      </PanelTitle>
                      {isInstalled && (
                        <div className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                          <Check className="h-3 w-3" />
                        </div>
                      )}
                      {!isInstalled && (
                        <div className="absolute right-1.5 top-1.5 hidden group-hover:flex h-4 w-4 items-center justify-center rounded-full bg-muted-foreground text-background">
                          <Download className="h-3 w-3" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </SectionCard>
        </div>
      </AsyncContent>
    </AdminSubPageLayout>
  );
}
